import { useEffect, useState } from "react";
import { api, resolveUploadUrl } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, ExternalLink, X } from "lucide-react";

const StoriesReel = () => {
  const { settings } = useSettings();
  const [stories, setStories] = useState([]);
  const [viewer, setViewer] = useState(null); // index

  useEffect(() => {
    let alive = true;
    api.get("/stories").then((r) => { if (alive) setStories(r.data || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (stories.length === 0) return null;

  const open = (idx) => setViewer(idx);
  const close = () => setViewer(null);
  const prev = () => setViewer((i) => (i > 0 ? i - 1 : stories.length - 1));
  const next = () => setViewer((i) => (i < stories.length - 1 ? i + 1 : 0));

  const current = viewer !== null ? stories[viewer] : null;

  return (
    <section className="animate-fade-up" data-testid="stories-reel">
      <div className="flex items-end justify-between mb-3">
        <div>
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#8A252C]/80 font-bold">
            <i className="fa-brands fa-instagram" aria-hidden="true" />
            Son Paylaşımlar
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#1F2937] mt-1">
            {settings.site_name || "ŞAHDER"} · Instagram
          </h2>
        </div>
        <span className="text-xs text-[#4B5563]">Tıklayın · sürükleyin</span>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scroll-smooth" data-testid="stories-scroll">
        {stories.map((s, i) => (
          <button
            key={s.id}
            type="button"
            onClick={() => open(i)}
            data-testid={`story-thumb-${s.id}`}
            className="shrink-0 group"
          >
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full p-[3px] bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF] shadow">
              <div className="w-full h-full rounded-full overflow-hidden ring-2 ring-white bg-white">
                <img
                  src={resolveUploadUrl(s.image_url)}
                  alt={s.caption || "Story"}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform"
                />
              </div>
            </div>
            {s.caption && (
              <div className="mt-1.5 text-[11px] text-[#4B5563] text-center truncate max-w-[80px] sm:max-w-[96px] mx-auto">
                {s.caption}
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Viewer */}
      <Dialog open={viewer !== null} onOpenChange={(o) => !o && close()}>
        <DialogContent
          className="max-w-md bg-black text-white border-0 p-0 overflow-hidden max-h-[92vh]"
          data-testid="story-viewer"
        >
          {current && (
            <div className="relative w-full aspect-[9/16] bg-black flex items-center justify-center">
              <img
                src={resolveUploadUrl(current.image_url)}
                alt={current.caption || "Story"}
                className="max-w-full max-h-full object-contain"
              />
              {/* Progress bars */}
              <div className="absolute top-2 left-2 right-2 flex gap-1">
                {stories.map((_, i) => (
                  <div key={i} className="flex-1 h-0.5 rounded-full bg-white/30 overflow-hidden">
                    <div className={`h-full bg-white transition-all ${i < viewer ? "w-full" : i === viewer ? "w-full" : "w-0"}`} />
                  </div>
                ))}
              </div>
              {/* Controls */}
              <button onClick={close} data-testid="story-close-btn"
                className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center">
                <X className="w-5 h-5" />
              </button>
              {stories.length > 1 && (
                <>
                  <button onClick={prev} data-testid="story-prev-btn"
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button onClick={next} data-testid="story-next-btn"
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center">
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
              {/* Footer */}
              {(current.caption || current.link) && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  {current.caption && (
                    <p className="text-sm">{current.caption}</p>
                  )}
                  {current.link && (
                    <a
                      href={current.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="story-link"
                      className="mt-2 inline-flex items-center gap-1 text-xs bg-white text-[#1F2937] rounded-full px-3 py-1.5 font-semibold hover:bg-white/90"
                    >
                      Bağlantıyı Aç
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default StoriesReel;
